import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import {
  typography,
  LightGreen,
  MediumGreen,
  DarkGreen,
  Gold,
  DarkGray,
  Copper,
  OffWhite,
  White,
} from './Styles';

const baseButtonStyles = css`
  ${typography}
  display: flex;
  align-items: center;
  border: none;
  margin-top: 20px;
  transition: background-color 0.5s ease;
  cursor: pointer;
  :focus {
    outline: 0;
  }
`;

const buttonPalette = {
  primary: {
    backgroundColor: MediumGreen,
    color: White,
    contrast: DarkGreen,
    reverse: LightGreen,
  },
  secondary: {
    backgroundColor: Gold,
    color: DarkGray,
    contrast: Copper,
    reverse: OffWhite,
  },
  danger: {
    backgroundColor: '#73312d',
    color: White,
    contrast: '#98ba56',
    reverse: '#647a38',
  },
};

const buttonVariants = {
  primary: css`
    background-color: ${buttonPalette.primary.backgroundColor};
    color: ${buttonPalette.primary.color};
    -webkit-font-smoothing: antialiased;
    :hover {
      background-color: ${buttonPalette.primary.contrast};
    }
    :focus, :active {
      background-color: ${buttonPalette.primary.contrast};
    }
  `,
  secondary: css`
    background-color: ${buttonPalette.secondary.backgroundColor};
    color: ${buttonPalette.secondary.color};
    :hover {
      background-color: ${buttonPalette.secondary.contrast};
    }
    :focus, :active {
      background-color: ${buttonPalette.secondary.contrast};
    }
  `,
  danger: css`
    background-color: ${buttonPalette.danger.backgroundColor};
    color: ${buttonPalette.danger.color};
    -webkit-font-smoothing: antialiased;
    :hover {
      background-color: ${buttonPalette.danger.contrast};
    }
    :focus, :active {
      background-color: ${buttonPalette.danger.contrast};
    }
  `,
};

const Button = ({
  onClick,
  children,
  disabled,
  extraButtonStyles = '',
  leftIcon,
  size = '1.3rem',
  to,
  variant = 'primary',
}) => {
  const childOffset = children
    ? `
      margin-right: calc(0.5*${size});
    `
    : '';
  const iconOffset = leftIcon
    ? ''
    : `
      margin-left: calc(0.5*${size});
    `;
  const guts = (
    <>
      {leftIcon
        && (
        <div
          css={css`
            background-color: ${buttonPalette[variant].reverse};
            color: ${buttonPalette[variant].contrast};
            border-radius: 100%;
            height: calc(1.5*${size});
            width: calc(1.5*${size});
            display: flex;
            align-items: center;
            justify-content: center;
            ${childOffset};
          `}
        >
          {leftIcon}
        </div>
        )}
      { children
        && (
        <div
          css={css`
            ${iconOffset}
            padding-right: calc(.5*${size});
          `}
        >
          {children}
        </div>
        )}
    </>
  );
  if (to) {
    if (onClick) {
      Console.warn('You cannot set both `to` and `onClick` on a Button. Using `to` only.', new Error());
    }
    if (disabled) {
      Console.warn('Disabled style is not supported when using `to`.', new Error());
    }
    return (
      <Link
        to={to}
        type="button"
        className="appearance-none"
        css={css`
          ${baseButtonStyles}
          ${extraButtonStyles}
          ${buttonVariants[variant]}
          font-family: "Aleo", serif;
          text-decoration: none;
          text-align: center;
          font-size: ${size};
          padding: calc(.5*${size}) calc(.5*${size});
          border-radius: 15px;
        `}
      >
        { guts }
      </Link>
    );
  }
  return (
    <button
      type="button"
      className="appearance-none"
      onClick={onClick}
      disabled={disabled}
      css={css`
        ${baseButtonStyles}
        ${extraButtonStyles}
        ${buttonVariants[variant]}
        font-size: ${size};
        padding: calc(.5*${size}) calc(.5*${size});
        border-radius: 15px;
      `}
    >
      { guts }
    </button>
  );
};

export default Button;
